import { ReactNode, useContext } from "react";
import { useAuth } from "react-oidc-context";

import ModalsManagerContext, { IModalsManager } from "contexts/ModalsManager";
import settingsService from "api/settingsService";
import companyKeysService from "api/companyKeysService";
import usePrivilegesStore from "hooks/usePrivilegesStore";
import { LanguageSwitcher } from "components/LanguageSwitcher/LanguageSwitcher";

import { getCompanyId as getWorkspaceId } from "utils/getCompanyId";
import {
  peekActiveCompanyKey,
  hasActiveCompanyKey,
} from "utils/encryptForCompany";
import { getUserClaim } from "utils/getUserClaim";
import { getUserIsActivated } from "utils/getUserIsActivated";
import { getUserIdentityVerified } from "utils/getUserIdentityVerified";
import { Claims } from "dto/User/claims";
import { ENotificationType, notification } from "utils/notification";
import { isDev } from "utils/isDev";

import styles from "./styles.module.scss";

const toast = (text: string, type = ENotificationType.SUCCESS) =>
  notification({ type, title: "DEBUG", text });

const copy = (value?: string | null) => {
  if (!value) {
    toast("Brak wartości do skopiowania", ENotificationType.INFO);
    return;
  }
  navigator.clipboard
    .writeText(value)
    .then(() => toast("Skopiowano do schowka"))
    .catch(() => toast("Nie udało się skopiować", ENotificationType.ERROR));
};

const Section = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <section className={styles.section}>
    <h2 className={styles.sectionTitle}>{title}</h2>
    <div className={styles.sectionBody}>{children}</div>
  </section>
);

const Row = ({
  k,
  v,
  copyable,
}: {
  k: string;
  v: ReactNode;
  copyable?: string | null;
}) => (
  <div className={styles.kv}>
    <span className={styles.k}>{k}</span>
    <span className={styles.v}>{v ?? "—"}</span>
    {copyable !== undefined && (
      <button
        type="button"
        className={styles.copyBtn}
        title="Kopiuj"
        onClick={() => copy(copyable)}
      >
        ⧉
      </button>
    )}
  </div>
);

const DebugView = () => {
  const auth = useAuth();
  const { modals, setModals } = useContext(ModalsManagerContext);

  const { data: initState } = settingsService.useGetInitialState();
  const { mutate: setFirstConfigCompleted } =
    settingsService.useSetInitialConfigurationCompleted();
  // Diagnostyka klucza firmy (model-kluczy-firmowych, F1/F2) — GET /workspace/key może
  // jeszcze nie istnieć po stronie BE, stąd jawne enabled=true tylko tutaj (DEBUG) i
  // obsłużony stan błędu niżej, zamiast domyślnie wyłączonego hooka.
  const { data: companyKeysData, isError: isCompanyKeysError } =
    companyKeysService.useGetCompanyKeys(true);

  // Store uprawnień jest nietypowany (state: unknown) — rzutujemy na znany kształt.
  const priv = usePrivilegesStore() as {
    active: string;
    privileges: string[];
    switchToFree: () => void;
    swtichToStandard: () => void;
    swtichToPremium: () => void;
  };
  const uiVariant = priv.active;
  const uiPrivileges = priv.privileges;
  const switchToFree = priv.switchToFree;
  const switchToStandard = priv.swtichToStandard;
  const switchToPremium = priv.swtichToPremium;

  if (!isDev) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.notDev}>
          Zakładka DEBUG jest dostępna tylko w środowisku deweloperskim.
        </div>
      </div>
    );
  }

  const openModal = (key: keyof IModalsManager) =>
    setModals({ ...modals, [key]: true, hide: false });

  const MODAL_BUTTONS: { key: keyof IModalsManager; label: string }[] = [
    { key: "initialConfig", label: "Kreator pierwszej aktywacji" },
    { key: "contactFormSettings", label: "ContactFormWizard" },
    { key: "settings", label: "Ustawienia" },
    { key: "recoveryCode", label: "Kod odzyskiwania" },
    { key: "createChat", label: "Utwórz czat" },
    { key: "createInvoice", label: "Utwórz fakturę" },
    { key: "createHelpdesk", label: "Zgłoszenie helpdesk" },
  ];

  const activeCompanyKey = peekActiveCompanyKey();
  const companyKeysView = companyKeysData?.data;
  const user = auth.user;
  const expiresAt = user?.expires_at
    ? new Date(user.expires_at * 1000).toLocaleString()
    : null;

  const env = import.meta.env;

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <h1 className={styles.h1}>DEBUG</h1>
        <span className={styles.badge}>
          {import.meta.env.DEV ? "vite dev" : `VITE_ENV=${env.VITE_ENV}`}
        </span>
        <div className={styles.headerSpacer} />
        <LanguageSwitcher />
        <button
          type="button"
          className={styles.btn}
          onClick={() => window.location.reload()}
        >
          Przeładuj aplikację
        </button>
      </div>

      <div className={styles.grid}>
        <Section title="Kreatory i modale">
          <div className={styles.btnRow}>
            {MODAL_BUTTONS.map((b) => (
              <button
                key={b.key}
                type="button"
                className={styles.btn}
                onClick={() => openModal(b.key)}
              >
                {b.label}
              </button>
            ))}
          </div>
          <p className={styles.hint}>
            Kreator pierwszej aktywacji pokazuje się automatycznie tylko gdy BE
            zwróci <code>ShowFirstConfiguration = true</code> (flaga
            <code> InitialConfiguration</code> niezapisana). Tutaj otwierasz go
            ręcznie.
          </p>
        </Section>

        <Section title="Pierwsza aktywacja (flaga)">
          <Row
            k="ShowFirstConfiguration"
            v={String(initState?.data?.ShowFirstConfiguration ?? "—")}
          />
          <div className={styles.btnRow}>
            <button
              type="button"
              className={styles.btn}
              onClick={() => openModal("initialConfig")}
            >
              Otwórz kreator
            </button>
            <button
              type="button"
              className={styles.btnDanger}
              onClick={() =>
                setFirstConfigCompleted(
                  {},
                  { onSuccess: () => toast("Oznaczono jako wykonaną") },
                )
              }
            >
              Oznacz jako wykonaną
            </button>
          </div>
          <p className={styles.hint}>
            Aby ponownie zobaczyć kreator automatycznie, trzeba zresetować flagę
            po stronie BE (np. <code>GET /api/activation/restart</code> na
            koncie testowym albo wyzerować bit <code>InitialConfiguration</code>{" "}
            w DB).
          </p>
        </Section>

        <Section title="Firma — klucz (diagnostyka)">
          <Row
            k="getWorkspaceId()"
            v={getWorkspaceId() ?? "—"}
            copyable={getWorkspaceId()}
          />
          <Row
            k="Mirror sesji ma aktywny klucz"
            v={String(hasActiveCompanyKey())}
          />
          <Row
            k="WorkspaceId (mirror)"
            v={activeCompanyKey?.WorkspaceId ?? "—"}
            copyable={activeCompanyKey?.WorkspaceId}
          />
          <Row
            k="WorkspaceKeyId (mirror)"
            v={activeCompanyKey?.WorkspaceKeyId ?? "—"}
            copyable={activeCompanyKey?.WorkspaceKeyId}
          />
          <Row k="Generacja (mirror)" v={activeCompanyKey?.Generation ?? "—"} />
          <p className={styles.hint}>
            Mirror (<code>utils/encryptForCompany</code>) zasila App.tsx przy
            starcie sesji — puste wartości poza faktycznym brakiem klucza mogą
            też znaczyć, że priming jeszcze nie wpięty (F1/F2 w toku). Poniżej
            stan wg BE (<code>GET /workspace/key</code>), niezależny od mirrora.
          </p>
          {isCompanyKeysError && (
            <Row k="GET /workspace/key" v="błąd / endpoint niedostępny" />
          )}
          {companyKeysView && (
            <>
              <Row
                k="ActiveKeyId (BE)"
                v={companyKeysView.ActiveKeyId ?? "—"}
              />
              {/* `Keys` bywa nieobecne w odpowiedzi (starsze BE, firma bez kluczy) — bez
                  domyślnej tablicy panel wywracał się dokładnie w przypadku, który ma
                  diagnozować. */}
              {(companyKeysView.Keys ?? []).map((k) => (
                <Row
                  key={k.Id}
                  k={`Generacja ${k.Generation}${
                    k.RetiredDate ? " (wycofana)" : ""
                  }`}
                  v={k.MyGrant ? "mam grant" : "BRAK grantu"}
                />
              ))}
              {!companyKeysView.Keys?.length && (
                <Row k="Generacje" v="brak (firma bez wydanego klucza)" />
              )}
            </>
          )}
        </Section>

        <Section title="Pakiet / moduły (symulacja UI)">
          <Row k="active" v={String(uiVariant)} />
          <div className={styles.btnRow}>
            <button type="button" className={styles.btn} onClick={switchToFree}>
              Bezpłatny
            </button>
            <button
              type="button"
              className={styles.btn}
              onClick={switchToStandard}
            >
              Standard
            </button>
            <button
              type="button"
              className={styles.btn}
              onClick={switchToPremium}
            >
              Premium
            </button>
          </div>
          <div className={styles.modules}>
            {uiPrivileges.map((m) => (
              <span key={m} className={styles.moduleChip}>
                {m}
              </span>
            ))}
          </div>
        </Section>

        <Section title="Użytkownik / token">
          <Row k="sub" v={user?.profile?.sub} copyable={user?.profile?.sub} />
          <Row k="name" v={user?.profile?.name} />
          <Row k="email" v={user?.profile?.email as string} />
          <Row k="role" v={(user?.profile?.role as string) ?? "—"} />
          <Row
            k="aktywowany"
            v={String(user ? getUserIsActivated(user) : "—")}
          />
          <Row
            k="tożsamość zweryfikowana"
            v={String(user ? getUserIdentityVerified(user) : "—")}
          />
          <Row
            k="status (claim)"
            v={user ? getUserClaim(user, Claims.UserStatusClaim) : "—"}
          />
          <Row k="token wygasa" v={expiresAt} />
          <div className={styles.btnRow}>
            <button
              type="button"
              className={styles.btn}
              onClick={() => copy(user?.access_token)}
            >
              Kopiuj access token
            </button>
          </div>
        </Section>

        <Section title="Środowisko">
          <Row
            k="VITE_APP_API_URL"
            v={env.VITE_APP_API_URL}
            copyable={env.VITE_APP_API_URL}
          />
          <Row k="VITE_APP_API_WS" v={env.VITE_APP_API_WS} />
          <Row k="VITE_APP_OIDC_AUTHORITY" v={env.VITE_APP_OIDC_AUTHORITY} />
          <Row k="VITE_APP_SITE" v={env.VITE_APP_SITE} />
          <Row k="VITE_ENV" v={env.VITE_ENV} />
          <Row k="MODE" v={env.MODE} />
        </Section>

        <Section title="Narzędzia">
          <div className={styles.btnRow}>
            <button
              type="button"
              className={styles.btn}
              onClick={() => {
                localStorage.removeItem("workspaceId");
                sessionStorage.removeItem("workspaceId");
                toast("Wyczyszczono workspaceId");
              }}
            >
              Wyczyść workspaceId
            </button>
            <button
              type="button"
              className={styles.btn}
              onClick={() => toast("Testowy sukces", ENotificationType.SUCCESS)}
            >
              Test: sukces
            </button>
            <button
              type="button"
              className={styles.btn}
              onClick={() =>
                toast("Testowa informacja", ENotificationType.INFO)
              }
            >
              Test: info
            </button>
            <button
              type="button"
              className={styles.btn}
              onClick={() => toast("Testowy błąd", ENotificationType.ERROR)}
            >
              Test: błąd
            </button>
          </div>
        </Section>
      </div>
    </div>
  );
};

export default DebugView;
